import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import { useDispatch, useSelector } from "react-redux";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import DeleteModal from "../../../Components/Common/DeleteModal";
import PriorityModal from "../../../Components/Common/Modal/PriorityModal";

// --- New: Import react-select ---
import Select from "react-select";
import FeatherIcon from "feather-icons-react";
import { fetchReportedIssuesClientUsers, fetchReportedTableColumnNames, fetchUserReportedIssues, updateUserPreference, deleteReportedIssues, fetchUserReportedChats, updateReportedIssueStatus } from "../../../slices/thunks";
import { getDashboardData } from "../../../slices/thunks";
import ChatHistoryAIChatModal from "../../../Components/Common/ChatHistoryAIChatModal";
import ChatCommentModal from "../../../Components/Common/Modal/ChatCommentModal";
import { useSearchParams } from "react-router-dom";
import { getAuthUser } from "../../../helpers/utils";

const priorityArr = [
    { ID: 1, Label: 'Low' },
    { ID: 2, Label: 'Medium' },
    { ID: 3, Label: 'High' }
]


const ReportedLegalIssues = () => {
    const { t } = useTranslation();
    const { user } = getAuthUser();
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState("");
    const [selectedSession, setSelectedSession] = useState({}); 
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [isLegal, setIsLegal] = useState("");
    const [activeTab, setActiveTab] = useState(searchParams.get("status") ? searchParams.get("status") : "new");
    const [commentModal, setCommentModal] = useState(false); 
    const [priorityModal, setPriorityModal] = useState(false); 
    const [selectedIds, setSelectedIds] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const today = new Date().toISOString().split("T")[0];

    const permission = (() => {
         return usePermissions(1, 13);
    })();

    const { chatSession, loading, chats, tableColumnNames, clientUsers, clientUsersloading, chatloading, headerLoading } = useSelector(
        (state) => ({
            tableColumnNames: state.ReportedIssues.tableColumnNames,
            loading: state.ReportedIssues.loading,
            headerLoading: state.ReportedIssues.headerLoading,
            chats: state.ReportedIssues.chats,
            chatSession: state.ReportedIssues.chatSession,
            clientUsers: state.ReportedIssues.clientUsers,
            clientUsersloading: state.ReportedIssues.clientUsersloading,
            chatloading: state.ReportedIssues.chatloading,
        })
    );

    const handleTabChange = (tab) => {
        const newParams = { status: tab };

        setSearchParams(newParams);
        setActiveTab(tab);
    }

    const handleCommentClick = (item) => {
        setSelectedSession(item);
        setCommentModal(true);  
    }

    const handlePriorityClick = (item) => {
        setSelectedSession(item);
        setPriorityModal(true);  
    }

    const handleCommentSubmit = (value) => {
        const data = {
          ChatID: selectedSession.ID, 
          Status: value.Status,
          Comment: value.Comment
        }
    
        dispatch(updateReportedIssueStatus(data));
        setSelectedSession({})
        setCommentModal(false);
    }

    const handlePrioritySubmit = (value) => {
        const data = {
          ChatID: selectedSession.ID, 
          Priority: value.Priority
        }
    
        dispatch(updateReportedIssueStatus(data));
        setSelectedSession({})
        setPriorityModal(false);
    }

    const handleSearch = (e) => {
        e.preventDefault();

        const payload = {};
        if (selectedCustomer) payload.UserID = selectedCustomer.value;
        if (fromDate) payload.FromDate = fromDate;
        if (toDate) payload.ToDate = toDate;
        if (isLegal != "") payload.IsLegal = isLegal;

        dispatch(fetchUserReportedIssues(payload));
    };

    const handleClear = (e) => {
        e.preventDefault();
        setSelectedCustomer(null); 
        setFromDate("");
        setToDate("");
        setIsLegal("");
        dispatch(fetchUserReportedIssues());
    };

    const handleViewHistory = (session) => {
        dispatch(fetchUserReportedChats({SessionID: session.SessionID, MessageID:session.MessageID}));
        setSelectedSession(session);
        setHistoryModalOpen(true);
    };

    const toggleHistoryModal = () => {
        setHistoryModalOpen(false);
        setSelectedSession({}); // Clear data when closing
    };

    const handleViewFeedback = (message) => {
        setFeedbackMessage(message);
        setFeedbackModalOpen(true);
    };

    const toggleFeedbackModal = () => {
        setFeedbackModalOpen(false);
        setFeedbackMessage("");
    };
    
    const saveUserPreferences = async () => {
        const preferencesPayload = {
        ColumnIDs: tableColumnNames.reduce((acc, column) => {
            acc[column.ID] = selectedColumns.includes(column.ID);
            return acc;
        }, {}),
        };

        setModalOpen(false);
        try {
        await dispatch(updateUserPreference(preferencesPayload)).unwrap();
        await dispatch(
            fetchReportedTableColumnNames({
            module: "REPORTED_LEGAL_ISSUE",
            })
        );

        } catch (error) {
        console.error("Error updating preferences:", error);
        }
    };

    const confirmDelete = () => {
        dispatch(deleteReportedIssues({ IDs: selectedIds }))
          .then(() => {
            setSelectedIds([]);
          })
          .catch((error) => {
            console.error("Error deleting:", error);
          });
        setDeleteModal(false);
    };

    const actionConfig = {
        showCheckboxColumn: false,
        // action: false,
        comment: { 
            icon: "ri-file-close-line",
            tooltip: t("File Issue"),
            onClick: handleCommentClick,
            isVisible: (item, perm) =>  true 
        },
        // priority: { 
        //     icon: "ri-information-line",
        //     tooltip: t("Priority"),
        //     onClick: handlePriorityClick,
        //     isVisible: (item, perm) =>  true 
        // },
        delete: {
            icon: "ri-delete-bin-2-line",
            tooltip: "Delete",
            isVisible: (item, perm) => user.RoleID == 1
        },
    };

    const Chats = useMemo(() => {
        if (!chatSession) return [];

        return chatSession.filter((item) => {
        let isMatch = false;

        switch (activeTab) {
            case "new":
                isMatch = item.Status == null || item.Status == 1;
                break;
            case "resolved":
                isMatch = item.Status == 3;
                break;
            case "not-resolved":
                isMatch = item.Status == 4;
                break;
            case "completed":
                isMatch = item.Status == 5;
                break;
           
            default:
                isMatch = true;
        }

        return isMatch;
        });
    }, [chatSession, activeTab]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch(getDashboardData());
                dispatch(fetchUserReportedIssues()); 
                dispatch(fetchReportedTableColumnNames({ module: "REPORTED_LEGAL_ISSUE" }));
                dispatch(fetchReportedIssuesClientUsers({ IsActive: true }));
            } catch (error) {
                console.error(error);
            }   
        };

        fetchData();
    }, [dispatch]);

    document.title = "Alitqan | AI Chat Report";

    return (
        <React.Fragment>
        <div className="page-content">
            <Container fluid={true}>
            <Row className="mb-3">
                <Col>
                    <BreadCrumb title={t("ReportedLegalIssues")} />
                </Col>
            </Row>

            <Row>
                <Col lg={12}>
                <Card>
                    <CardHeader>
                    <h5 className="card-title mb-0">{t("SearchFilters")}</h5>
                    </CardHeader>
                    <CardBody>
                    <Form onSubmit={handleSearch}>
                        <Row className="g-3">
                        <Col md={3}>
                            <Label for="customer-search">{t("Customer")}</Label>
                            {clientUsersloading ? (
                                <div className="d-flex align-items-center" style={{ minHeight: 38 }}>
                                    <Spinner size="sm" color="primary" className="me-2" />
                                    {t("Loading...")}
                                </div>
                            ) : (
                                <Select
                                    id="customer-search"
                                    name="customer"
                                    options={clientUsers.map(u => ({
                                        value: u.ID || u.Id,
                                        label: u.UserName || u.Email || u.Phone
                                    }))}
                                    value={selectedCustomer}
                                    onChange={(selectedOption) => setSelectedCustomer(selectedOption)}
                                    placeholder={t("SearchAndSelectCustomer")}
                                    isClearable={true}
                                    isSearchable={true}
                                    classNamePrefix="select2-selection"
                                    noOptionsMessage={() => t("NoOptions")}
                                />
                            )}
                        </Col>

                        <Col md={4}>
                            <Label>{t("DateLastMessage")}</Label>
                            <div className="d-flex gap-2">
                                {/* // --- Modified: Added value and onChange --- */}
                                <Input type="date" name="dateFrom" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={today} />
                                <Input type="date" name="dateTo" value={toDate} onChange={(e) => setToDate(e.target.value)} max={today} />
                            </div>
                        </Col>

                        <Col md={2}>
                            <Label>{t("Chat Type")}</Label>
                            <Input
                                type="select"
                                value={isLegal}
                                onChange={(e) => setIsLegal(e.target.value)}
                                className="form-control"
                            >
                                <option value="">{t('Select')}</option>
                                <option value={true}>{t('Legal')}</option>
                                <option value={false}>{t('Cases')}</option>
                            </Input>
                        </Col>

                        {/* <Col md={3}>
                            <Label>{t("Priority")}</Label>
                            <Input
                                type="select"
                                value={priority}
                                onChange={(e) => setPriority(e.target.value)}
                                className="form-control"
                            >
                                <option value="">{t('Select')}</option>
                                { priorityArr.map((item) => {
                                    return <option value={item.ID}>{t(item.Label)}</option>
                                })}
                            </Input>
                        </Col> */}
                        
                        <Col md={12} className="text-end ">
                            {/* // --- Modified: Added onClick handlers --- */}
                            <Button type="submit" color="primary" className="me-2">
                                <i className="ri-search-line align-bottom me-1"></i> {t("Search")}
                            </Button>
                            <Button color="light" className="me-2" onClick={handleClear}>
                                <i className="ri-close-circle-line align-bottom me-1"></i> {t("Clear")}
                            </Button>
                        </Col>
                        </Row>
                    </Form>
                    {/* <div className="row mt-3">
                        {ChatCount.map((item, idx) => (
                        <div className="col-lg-3" key={idx}>
                            <div className="card">
                                <div className="card-body d-flex gap-3 align-items-center">
                                    <div className="avatar-sm">
                                        <div className={`avatar-title border bg-${item.BgColor}-subtle border-${item.BgColor} border-opacity-25 rounded-2 fs-17`}>
                                            <FeatherIcon icon={item.Icon} className={`icon-dual-${item.BgColor}`}></FeatherIcon>
                                        </div>
                                    </div>
                                    <div className="flex-grow-1">
                                        <h5 className="fs-15">{item.Count}</h5>
                                        <p className="mb-0 text-muted">{t(item.Label)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        ))}
                    </div> */}
                    </CardBody>
                </Card>
                </Col>
            </Row>

            <div className="table-responsive">
                <Card>
                    <CardHeader>
                        <Row className="g-4 ">
                            <Col className="col-sm-auto">
                            {/* <div>
                                <h5 className="card-title mb-0">{t("ChatSession")}</h5>
                            </div> */}
                            </Col>
                            <Col className="col-sm">
                            <div className="d-flex justify-content-sm-end">
                                <Button
                                    className="btn btn-soft"
                                    onClick={() => setModalOpen(true)}
                                    disabled={loading || headerLoading}
                                >
                                <i className="ri-filter-3-line"></i>
                                </Button>
                                <div className="search-box ms-2 me-2">
                                <Input
                                    type="text"
                                    className="form-control search"
                                    placeholder={t("Search here")}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <i className="ri-search-line search-icon"></i>
                                </div>
                            </div>
                            </Col>
                        </Row>
                    </CardHeader>
                    <CardBody>
                        {   (loading || headerLoading) ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <Spinner style={{ height: "3rem", width: "3rem" }} className="me-2">
                            Loading...
                            </Spinner>
                        </div>
                        ) : (
                        <div className="listjs-table" id="customerList">
                            <Nav tabs style={{ marginBottom: 16 }}>
                                <NavItem>
                                    <NavLink
                                    className={activeTab === "new" ? "active" : ""}
                                    onClick={() => handleTabChange("new")}
                                    style={{ cursor: "pointer" }}
                                    >
                                    {t('New')}
                                </NavLink>
                                </NavItem>
                                    <NavItem>
                                    <NavLink
                                    className={activeTab === "resolved" ? "active" : ""}
                                    onClick={() => handleTabChange("resolved")}
                                    style={{ cursor: "pointer" }}
                                    >
                                    {t('Resolved')}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                    className={activeTab === "not-resolved" ? "active" : ""}
                                    onClick={() => handleTabChange("not-resolved")}
                                    style={{ cursor: "pointer" }}
                                    >
                                    {t('Not Resolved')}
                                    </NavLink>
                                </NavItem>
                                    <NavItem>
                                    <NavLink
                                    className={activeTab === "completed" ? "active" : ""}
                                    onClick={() => handleTabChange("completed")}
                                    style={{ cursor: "pointer" }}
                                    >
                                    {t('Completed')}
                                    </NavLink>
                                </NavItem>
                            </Nav>

                            <CommonDataTable
                                data={Chats}
                                tableColumnNames={tableColumnNames}
                                searchTerm={searchTerm}
                                actionConfig={actionConfig}
                                permission={permission}
                                setDeleteModal={setDeleteModal}
                                setSelectedIds={setSelectedIds}
                                onViewHistory={handleViewHistory}
                                onViewFeedback={handleViewFeedback}
                            />
                        </div>
                        )}
                    </CardBody>
                </Card>
            </div>
            </Container>

            <ColumnSelectionModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(!modalOpen)}
                columns={tableColumnNames}
                selectedColumns={selectedColumns}
                setSelectedColumns={setSelectedColumns}
                handleSave={saveUserPreferences}
            />

            
            {/* Delete modal  */}
            <DeleteModal
                showDelete={deleteModal}
                setShowDelete={setDeleteModal}
                onDeleteClick={confirmDelete}
                setSelectedIds={setSelectedIds}
                // description={
                //   selectedIds.length > 1
                //     ? `Are you sure you want to delete ${selectedIds.length} selected items?`
                //     : `Are you sure you want to delete this record?`
                // }
                description={t("confirmDelete")}
            />

            {/* It will only be visible when historyModalOpen is true */}
            <ChatHistoryAIChatModal
                isOpen={historyModalOpen}
                toggle={toggleHistoryModal}
                session={selectedSession || {}}
                loading={chatloading}
                ReportedLegalIssues={true}
                messages={chats}
                chatType="ai"
            />

            <Modal isOpen={feedbackModalOpen} toggle={toggleFeedbackModal} centered>
                <ModalHeader toggle={toggleFeedbackModal}>
                    {t("FeedbackDetails")}
                </ModalHeader>
                <ModalBody>
                    <p className="text-muted">{feedbackMessage}</p>
                </ModalBody>
            </Modal>

            <ChatCommentModal
                title={t("Comment")}
                show={commentModal}
                selectedRow={selectedSession} 
                setShow={setCommentModal}
                onSubmitHandler={handleCommentSubmit}
            />

            <PriorityModal
                show={priorityModal}
                setShow={setPriorityModal}
                selectedRow={selectedSession} 
                priority={priorityArr}
                onSubmit={handlePrioritySubmit}
            />

        </div>
        </React.Fragment>
    );
};

export default ReportedLegalIssues;